import Chapter from "src/objects/Chapter";
import Story from "src/objects/Story";

interface GameStates {  
    unset:string;
    continue:string;
    restart:string;
}

interface LandingIntroText {  
    subHeading:string;
    title:string;
    description:string;
}

interface GameIntroText {  
    gameOver:string;
    playerDied:string;
}

interface IntroText {  
    landing:LandingIntroText;
    game:GameIntroText;
}

interface DefaultChapter {  
    introText:string;
    objectiveText:string;
    characterText:string;
    missionText:string;
}

interface DefaultChapters {  
    treasureHunter:DefaultChapter;
    cityHunter:DefaultChapter;
    cyberSecurity:DefaultChapter;
}

interface Colors {  
    green:string;
    red:string;
    purple:string;
    teal:string;
    grey:string;
    lightGrey:string;
    redShades:ColorShades;
    greenShades:ColorShades;
}

interface ColorShades {  
    c1:string;
    c2:string;
    c3:string;
    c4:string;
    c5:string;
    c6:string;
}

export default class AppConfig {
    
    public static BUILD_NUMBER:string = "1.0.7"; // Major Number, Minor Number, Build Number
    public static TOOLTIP_DELAY:number = 500;
    public static INTRO_LENGTH:number = 4; // total stories in the intro

    public static gameStates:GameStates = {
        unset: "unset",
        continue: "continue",
        restart: "restart",
    }

    public static colors:Colors = {
        green: "#97e4d5", // green
        red: "#f47661", // red
        purple: "#6F69B4", // purple
        teal: "#5AA5EF", // teal
        grey: "#6a6c6a", // grey
        lightGrey: "#F1F3F6", // light grey
        redShades: {
            c1: "#FF7B65",
            c2: "#EF735F",
            c3: "#E66F5A",
            c4: "#D76855",
            c5: "#C05D4C",
            c6: "#AE5445",
        },
        greenShades: {
            c1: "#9FF0E1",
            c2: "#97E4D5",
            c3: "#89D0C2",
            c4: "#81C4B7",
            c5: "#73AFA4",
            c6: "#669D93",
        },
    }

    public static gameLoaded:boolean = false;
    public static gameState:string = AppConfig.gameStates.unset;

    public static text:IntroText = {
        landing: {
            subHeading: "Do you know...",
            title: "Insert Title",
            description: "You can add a title and description for your learning game in the admin settings",
        },
        game: {
            gameOver: "<h2>Congratulations!</h2>You've made it to the end!",
            playerDied: "<h2>Bad Decision!</h2>You haven't made it to the end!",
        }
    }

    // THIS MUST MATCH THE 'THEME NAME' in the game
    public static SETTINGS_THEME_NAMES = {
        treasureHunter: "Cave Exploration",
        cityHunter: "City Dash",
        cyberSecurity: "Cyber Security",
        bridgeBuilder: "Bridge Building",
        hospital: "Hospital",
        army: "Military",
        construction: "Construction",
        office: "Office",
        school: "School",
        marsExploration: "Mars Exploration",
    }

    public static getDefaultChapterSettings(theme:String):DefaultChapter {
        let defaultChapter:DefaultChapter;
        switch (theme) {
            case AppConfig.SETTINGS_THEME_NAMES.cyberSecurity:
                defaultChapter = AppConfig.SETTINGS_DEFAULT_CHAPTERS.cyberSecurity;
                break;
            case AppConfig.SETTINGS_THEME_NAMES.treasureHunter:
                defaultChapter = AppConfig.SETTINGS_DEFAULT_CHAPTERS.treasureHunter;
                break;
            case AppConfig.SETTINGS_THEME_NAMES.cityHunter:
                defaultChapter = AppConfig.SETTINGS_DEFAULT_CHAPTERS.cityHunter;
                break;
            default:
                console.log("getDefaultChapter() Unknown chapter: ", theme)
                break;
        }
        return defaultChapter;
    }

    public static SETTINGS_DEFAULT_CHAPTERS:DefaultChapters = {
        cyberSecurity: {
            introText:"<p><span class=\"ql-size-large\">Welcome {name} to our cyber mainframe!</span></p><p>This game will simulate a cyber attack on our company and test your cyber situational awareness</p>",
            objectiveText:"<p><span class=\"ql-size-large\"><span class=\"ql-emojiblot\" data-name=\"warning\">﻿<span contenteditable=\"false\"><span class=\"ap ap-warning\">⚠</span></span>﻿</span> Security Alert <span class=\"ql-emojiblot\" data-name=\"warning\">﻿<span contenteditable=\"false\"><span class=\"ap ap-warning\">⚠</span></span>﻿</span></span></p><p>A hacker is attempting to steal our data...</p>",
            characterText:"<p>Take control of our <strong style=\"color: rgb(18, 196, 196);\">Cyber Defence Bot </strong> and stop the hacker before our data is stolen</p>",
            missionText:"<p><strong style=\"color: rgb(230, 104, 130);\">Poor decisions</strong> will make us more vulnerable, <strong style=\"color: rgb(147, 207, 71);\">good decisions</strong> will boost our security.</p>",
        },
        cityHunter: {
            introText:"string",
            objectiveText:"string",
            characterText:"string",
            missionText:"string",
        },
        treasureHunter: {
            introText:"string",
            objectiveText:"string",
            characterText:"string",
            missionText:"string",
        }
    }

    public static SETTINGS_VEHICLE_NAMES = {
        Car: "Car",
        Taxi: "Taxi",
        FireEngine: "Fire Engine",
        Ambulance: "Ambulance",
        PoliceCar: "Police Car",
        Bus: "Bus",
    }

    public static SETTINGS_STAT_NAMES = {
        Health: "Health",
        Money: "Money",
        Security: "Security",
        Economy: "Economy",
        Popularity: "Popularity",
        Satisfaction: "Satisfaction",
        People: "People",
        Barrels: "Barrels",
        Oil: "Oil",
        Fire: "Fire",
        Efficiency: "Efficiency",
        Speed: "Speed",
        Weight: "Weight",
    }

    public static SETTINGS_CURRENCY_NAMES = {
        GBP: "£",
        USD: "$",
        EURO: "€",
        BITCOIM: "₿",
        NONE: ""
    }

    // public static settings:any = {
    //     theme: AppConfig.SETTINGS_THEME_NAMES.treasureHunter,
    //     vehicle: AppConfig.SETTINGS_VEHICLE_NAMES.Bus,
    //     stat: AppConfig.SETTINGS_STAT_NAMES.Health,
    //     prefix: "",
    //     suffix: "",
    // }

    public static MOBILE_WIDTH:number = 500;
    public static INCOGNITO:boolean = true;

    // public static STATE_GAME_OVER_FAIL:string = "Game Over Fail";
    // public static STATE_GAME_OVER_RESTART:string = "Restart Game";
    // public static STATE_NEXT_CHAPTER:string = "Next Chapter";
    // public static STATE_GAME_OVER_SUCCESS:string = "Game Over Success";

    public static COPY_NO_STORY_TEXT:string = "<h2>Dead End!</h2>Oops! If you are the author of this game please add a page for this decision";
    public static COPY_MILESTONE_TEXT:string = "We've reached a milestone! Let's see how you're doing..."

    public static userData:any;
}


// WEBPACK FOOTER //
// ./src/config/AppConfig.tsx